<template>
	<main class="checkout-flow b7:flex -mx-5 -mb-5">
		<div v-if="loading || !plan" class="checkout-flow__loader flex justify-center items-center py-5 w-full">
			<Inline svg="loading" class="spin" />
		</div>
		<template v-else>
			<div class="checkout-flow__container">
				<CChangePlanNav v-if="showCustomizeBreadcrumb" :step="step" @customize="prevStep" @checkout="nextStep" />
				<section v-show="step === 'customize'" class="checkout-flow__main-section">
					<h2 class="font-display text-6 mb-4">Total Users</h2>
					<p class="text-3 my-4">{{ plan.DisplayName }} includes {{ plan.UserLimit }} users. Additional users are ${{ plan.AdditionalUserFee }}/user/month billed {{ isAnnual ? 'annually' : 'monthly' }}.</p>
					<CCustomizePlanAddon
						v-model="quantities.users"
						title="Users"
						label="users"
						icon="complex-icons/account-organization"
						info="Add more users to your plan for just $5/user/month."
						:min="addOnMinimums.users"
						:max="usersMax"
						:recommended-value="suggestedUsage.users"
						:show-recommended="hasAverage.users"
						:pricing="addOnPricingData.users.pricing"
						:included="addOnPricingData.users.included"
					/>
					<CWalletCustomizeSection
						class="mt-5"
						:wallet-add-ons="walletAddOns"
						:quantities="quantities"
						:average-rows="averageRows"
						:usage-window-label="usageWindowLabel"
						:plan="plan"
						:current-plan-name="currentPlanName"
						:show-suggested-amounts="hasSuggestedAmounts"
						@update-quantity="onUpdateQuantity"
					/>
				</section>
				<section v-show="step === 'checkout'" class="checkout-flow__main-section b7:mb-5">
					<template v-if="!costModel">
						<div v-for="n in 6" :key="n" class="skeleton" :class="n % 2 === 0 ? 'mb-4' : 'mb-3'"></div>
					</template>
					<CCheckoutBilling
						v-show="costModel"
						ref="changePlan"
						:plan="planCode"
						:annual-commitments="annualCommitments"
						@cost-updated="onCostUpdated"
						@cost-loading="costLoading = $event"
						@cost-throttled="costThrottled = $event"
						@change-plan-status="submitStatus = $event"
						@handle-close="prevStep"
					/>
				</section>
			</div>
			<CChangePlanSidebar
				:plan="plan"
				:current-plan-name="currentPlanName"
				:current-plan-is-annual="currentPlanIsAnnual"
				:annual-monthly-price="annualMonthlyPrice"
				:annual-savings-percent="annualSavingsPercent"
				:interval.sync="selectedInterval"
				:step="step"
				:show-customize="showCustomizeStep"
				:show-interval-toggle="showIntervalToggle"
				:users="quantities.users"
				:user-total="userTotal"
				:cost-model="costModel"
				:cost-loading="costLoading"
				:cost-throttled="costThrottled"
				:cost-stale="costStale"
				:submit-status="submitStatus"
				@next="nextStep"
				@submit="submitCheckout"
				@reset-status="submitStatus = 'default'"
				@customize="prevStep"
			/>
		</template>
	</main>
</template>

<script lang="ts">
	import { defineComponent, ref, reactive, computed, watch, onMounted } from 'vue';
	import { getPricing } from '@cognitoforms/api/services/plan-service';
	import { getOrganizationUsage, getOrganizationUsageAnnualAverage } from '@cognitoforms/api/services/settings/organization-settings-service';
	import type { Plan } from '@cognitoforms/types/server-types/plans/model/plan';
	import type { PricingTier } from '@cognitoforms/types/server-types/plans/model/pricing-tier';
	import type { UsageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-view-model';
	import type { UsageAnnualAverageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-annual-average-view-model';
	import type { SubscriptionCostViewModel } from '@cognitoforms/api/services/subscription-service';
	import { LineItemType } from '@cognitoforms/types/server-types/plans/model/line-item-type';
	import { getAnnualSavingsPercent } from '@cognitoforms/utils/variant-plans';
	import type { BillingMode } from 'src/components/pricing/billing-mode';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import CChangePlanNav from './ChangePlanNav.vue';
	import CCustomizePlanAddon from './CustomizePlanAddon.vue';
	import CChangePlanSidebar from './ChangePlanSidebar.vue';
	import CCheckoutBilling from './CheckoutBilling.vue';
	import CWalletCustomizeSection from './WalletCustomizeSection.vue';
	import { useWalletAddOns, GUESTS_MAX, DOCUMENTS_MAX, STORAGE_MAX, API_MAX, type WalletQuantities } from './use-wallet-addons';
	import type { AnnualCommitments } from './use-checkout-billing';
	import { useRouter, useRoute } from 'vue-router/composables';
	import { startUpgradeStage } from 'src/utilities/journey/activation';
	import { resolveCurrentPlanDimensions } from 'src/utilities/journey/registry';
	import debounce from 'src/utilities/debounce';

	const MONTHS_PER_YEAR = 12;
	const USERS_MAX = 500;
	const FUNDS_MAX = 100000;

	const selectionParams: { key: string; model: keyof WalletQuantities; decimals: number }[] = [
		{ key: 'users', model: 'users', decimals: 0 },
		{ key: 'guests', model: 'guests', decimals: 0 },
		{ key: 'documents', model: 'documentGeneration', decimals: 0 },
		{ key: 'storage', model: 'storage', decimals: 0 },
		{ key: 'api', model: 'apiUsage', decimals: 0 },
		{ key: 'funds', model: 'additionalFunds', decimals: 2 }
	];

	export default defineComponent({
		components: {
			CChangePlanNav,
			CCustomizePlanAddon,
			CChangePlanSidebar,
			CCheckoutBilling,
			CWalletCustomizeSection
		},
		props: {
			planCode: {
				type: String,
				default: ''
			},
			interval: {
				type: String,
				default: 'annual'
			}
		},
		setup(props) {
			const router = useRouter();
			const route = useRoute();

			const selectedInterval = ref<BillingMode>(props.interval === 'monthly' ? 'monthly' : 'annual');
			const isAnnual = computed(() => selectedInterval.value === 'annual');

			const queryNumber = (key: string) => {
				const raw = route.query[key];
				const single = Array.isArray(raw) ? raw[0] : raw;
				if (single == null || single === '')
					return null;
				const value = Number(single);
				return Number.isFinite(value) && value >= 0 ? value : null;
			};

			const queryString = (key: string) => {
				const raw = route.query[key];
				const single = Array.isArray(raw) ? raw[0] : raw;
				return single || undefined;
			};

			const hasPrefilledSelections = selectionParams.some(({ key }) => queryNumber(key) !== null);

			const step = ref<'customize' | 'checkout'>(isAnnual.value ? 'customize' : 'checkout');

			const loading = ref(true);

			const pricingTier = ref<PricingTier | null>(null);
			const currentPlan = ref<Plan | null>(null);
			const currentPlanIsTrial = ref(false);
			const currentPlanIsAnnual = ref(false);
			const currentPlanName = computed(() => currentPlan.value?.Name ?? null);

			const isFirstPaidPlan = ref(false);
			const quotedArm = ref<string | null>(null);
			const quotedExperimentCampaign = ref<string | null>(null);

			const loadPricingTier = async () => {
				const pricing = await getPricing();
				pricingTier.value = pricing.currentPricingTier;
				currentPlan.value = pricing.currentPlan ?? null;
				currentPlanIsTrial.value = pricing.currentPlanIsTrial === true;
				currentPlanIsAnnual.value = pricing.isAnnual === true;
				isFirstPaidPlan.value = pricing.isFirstPaidPlan === true;
				quotedArm.value = pricing.quotedArm ?? null;
				quotedExperimentCampaign.value = pricing.quotedExperimentCampaign ?? null;
			};

			const currentUsage = ref<UsageViewModel | null>(null);

			const loadCurrentUsage = async () => {
				currentUsage.value = await getOrganizationUsage();
			};

			const usageAverages = ref<UsageAnnualAverageViewModel | null>(null);

			const loadUsageAverages = async () => {
				usageAverages.value = await getOrganizationUsageAnnualAverage();
			};

			const plan = computed<Plan | null>(() =>
				pricingTier.value?.Plans.find(p => p.Name === props.planCode) ?? null);

			// Plans without an annual option fall back to the monthly rate,
			// which zeroes out the displayed discount.
			const annualMonthlyPrice = computed(() => plan.value?.AnnualMonthlyAmount ?? plan.value?.MonthlyAmount ?? 0);

			const annualSavingsPercent = computed(() => getAnnualSavingsPercent(pricingTier.value));

			const quantities = reactive<WalletQuantities>({
				users: 0,
				guests: 0,
				documentGeneration: 0,
				storage: 0,
				apiUsage: 0,
				additionalFunds: 0
			});

			const costModel = ref<SubscriptionCostViewModel | null>(null);
			const costLoading = ref(false);
			const costThrottled = ref(false);
			const costStale = ref(false);

			const annualFundingMonths = computed(() => {
				const lineItems = costModel.value?.invoice?.LineItems;
				if (!lineItems)
					return null;
				const monthlyRate = annualMonthlyPrice.value;
				const wholeMonthFunding = monthlyRate > 0 && lineItems.find(item =>
					item.Type === LineItemType.AnnualPlanCommitFunding
					&& Math.abs(item.Amount - monthlyRate * item.Quantity) < 0.01);
				return wholeMonthFunding ? wholeMonthFunding.Quantity : 0;
			});

			const annualMonths = computed(() => isAnnual.value ? (annualFundingMonths.value ?? MONTHS_PER_YEAR) : 1);

			const committedUsers = computed(() => isAnnual.value ? (costModel.value?.committedUsers ?? 0) : 0);

			const {
				addOnMinimums,
				monthlyAverages,
				suggestedUsage,
				addOnPricingData,
				showGuestsAddOn,
				showDocumentsAddOn,
				showStorageAddOn,
				showApiAddOn,
				currentUsageFor,
				walletAddOns,
				averageRows,
				usageWindowLabel,
				userTotal,
				walletTotal
			} = useWalletAddOns({
				plan,
				currentUsage,
				usageAverages,
				quantities,
				multiplier: annualMonths,
				includeUsers: true,
				committedUsers
			});

			const selectionBounds = computed<Record<keyof WalletQuantities, { min: number; max: number }>>(() => ({
				users: { min: addOnMinimums.value.users, max: USERS_MAX },
				guests: { min: addOnMinimums.value.guests, max: GUESTS_MAX },
				documentGeneration: { min: addOnMinimums.value.documents, max: DOCUMENTS_MAX },
				storage: { min: addOnMinimums.value.storageGb, max: STORAGE_MAX },
				apiUsage: { min: plan.value?.ApiUsageLimit ?? 0, max: API_MAX },
				additionalFunds: { min: 0, max: FUNDS_MAX }
			}));

			const defaultSelections = computed<Record<keyof WalletQuantities, number>>(() => ({
				users: suggestedUsage.value.users,
				guests: suggestedUsage.value.guests,
				documentGeneration: suggestedUsage.value.documents,
				storage: suggestedUsage.value.storageGb,
				apiUsage: suggestedUsage.value.apiUsage,
				additionalFunds: 0
			}));

			// Seed the quantities once pricing and historical usage resolve, then let any
			// selections carried by the URL override the suggested defaults.
			function seedSelections() {
				const s = suggestedUsage.value;
				quantities.users = s.users;
				quantities.guests = s.guests;
				quantities.documentGeneration = s.documents;
				quantities.storage = s.storageGb;
				quantities.apiUsage = s.apiUsage;
				for (const { key, model, decimals } of selectionParams) {
					const value = queryNumber(key);
					if (value === null)
						continue;
					const { min, max } = selectionBounds.value[model];
					quantities[model] = parseFloat(Math.min(Math.max(value, min), max).toFixed(decimals));
				}
			}

			watch(suggestedUsage, seedSelections);

			const usageExceedsIncluded = computed(() => {
				const data = addOnPricingData.value;
				const averages = monthlyAverages.value;
				return Math.max(averages.users, currentUsageFor('Users')) > data.users.included
					|| walletUsageExceedsIncluded.value;
			});

			const walletUsageExceedsIncluded = computed(() => {
				const data = addOnPricingData.value;
				const averages = monthlyAverages.value;
				return (showGuestsAddOn.value && Math.max(averages.guests, currentUsageFor('Guests')) > data.guests.included)
					|| (showDocumentsAddOn.value && Math.max(averages.documents, currentUsageFor('Document Templates')) > data.documents.included)
					|| (showStorageAddOn.value && Math.max(averages.storageGb, currentUsageFor('Storage')) > data.storage.included)
					|| (showApiAddOn.value && Math.max(averages.apiUsage, currentUsageFor('API Usage')) > data.api.included);
			});

			const hasSuggestedAmounts = computed(() => {
				const avg = monthlyAverages.value;
				return (showGuestsAddOn.value && avg.guests > 0)
					|| (showDocumentsAddOn.value && avg.documents > 0)
					|| (showStorageAddOn.value && avg.storageGb > 0)
					|| (showApiAddOn.value && avg.apiUsage > 0);
			});

			const hasAverage = computed(() => ({
				users: monthlyAverages.value.users > 0,
				guests: monthlyAverages.value.guests > 0,
				documents: monthlyAverages.value.documents > 0,
				storageGb: monthlyAverages.value.storageGb > 0,
				apiUsage: monthlyAverages.value.apiUsage > 0
			}));

			const isProductDowngrade = computed(() => {
				if (currentPlanIsTrial.value)
					return false;
				const targetProductIndex = plan.value?.Product?.Index;
				const currentProductIndex = currentPlan.value?.Product?.Index;
				if (targetProductIndex == null || currentProductIndex == null)
					return false;
				return targetProductIndex < currentProductIndex;
			});

			const showCustomizeStep = computed(() => isAnnual.value && usageExceedsIncluded.value && annualFundingMonths.value !== 0 && !isProductDowngrade.value);
			const showCustomizeBreadcrumb = computed(() => isAnnual.value && annualFundingMonths.value !== 0 && !isProductDowngrade.value);

			// Plans without an annual rate are restricted to monthly billing.
			// Only enforced once the plan resolves so the annual default isn't reset while loading.
			const monthlyOnly = computed(() => plan.value != null && plan.value.AnnualMonthlyAmount == null);

			// Hide the toggle on a same-tier checkout (ex. Team Annual -> Team Monthly and vice versa),
			// and for monthly-only plans.
			const showIntervalToggle = computed(() => !monthlyOnly.value && currentPlanName.value !== props.planCode);

			watch(monthlyOnly, (restricted) => {
				if (restricted)
					selectedInterval.value = 'monthly';
			}, { immediate: true });

			onMounted(async () => {
				try {
					await Promise.all([loadPricingTier(), loadUsageAverages(), loadCurrentUsage()]);

					if (isFirstPaidPlan.value)
						startUpgradeStage(queryString('source'), queryString('details'), false, resolveCurrentPlanDimensions(currentPlanName.value, null, quotedArm.value, quotedExperimentCampaign.value));

					// The checkout flow requires a resolved plan; an unknown or missing plan
					// code means the user landed here without a valid selection.
					if (!plan.value) {
						router.replace({ name: 'organization-settings-plan', params: route.params });
						return;
					}

					seedSelections();

					if (!showCustomizeStep.value || hasPrefilledSelections)
						step.value = 'checkout';
					commitSelections();
				}
				finally {
					loading.value = false;
				}
			});

			const annualCommitments = ref<AnnualCommitments | null>(
				isAnnual.value ? { isAnnual: true, users: quantities.users, wallet: walletTotal.value } : { isAnnual: false, users: null, wallet: null });

			function syncSelectionQuery() {
				const current = route.query;
				const query = { ...current };
				for (const { key, model } of selectionParams) {
					const value = quantities[model] ?? 0;
					if (value !== defaultSelections.value[model])
						query[key] = String(value);
					else
						delete query[key];
				}
				const routeInterval = props.interval === 'monthly' ? 'monthly' : 'annual';
				if (!monthlyOnly.value && selectedInterval.value !== routeInterval)
					query.interval = selectedInterval.value;
				const changed = Object.keys(query).length !== Object.keys(current).length
					|| Object.keys(query).some(key => query[key] !== current[key]);
				if (changed)
					router.replace({ query });
			}

			function commitSelections() {
				const next: AnnualCommitments = isAnnual.value
					? { isAnnual: true, users: quantities.users, wallet: walletTotal.value }
					: { isAnnual: false, users: null, wallet: null };
				const prior = annualCommitments.value;
				if (prior?.users !== next?.users || prior?.wallet !== next?.wallet)
					costStale.value = true;
				annualCommitments.value = next;
				syncSelectionQuery();
			}

			watch(selectedInterval, commitSelections);

			watch(showCustomizeStep, show => {
				if (!show && step.value === 'customize')
					nextStep();
			});

			function onCostUpdated(model: SubscriptionCostViewModel) {
				costModel.value = model;
				costStale.value = false;
			}

			// Debounce to avoid excessive cost recalculation requests when dragging sliders
			const debouncedCommitSelections = debounce(commitSelections, 400);
			function onUpdateQuantity({ model, value }: { model: keyof WalletQuantities; value: number }) {
				quantities[model] = value;
				if (!loading.value && step.value === 'customize')
					debouncedCommitSelections();
			}

			function nextStep() {
				if (step.value === 'customize') {
					commitSelections();
					step.value = 'checkout';
				}
			}

			function prevStep() {
				if (step.value === 'checkout' && showCustomizeBreadcrumb.value)
					step.value = 'customize';
			}

			const changePlan = ref<InstanceType<typeof CCheckoutBilling> | null>(null);
			const submitStatus = ref<ButtonStatus>('default');

			function submitCheckout() {
				changePlan.value?.submit();
			}

			return {
				step,
				loading,
				showCustomizeStep,
				showCustomizeBreadcrumb,
				hasSuggestedAmounts,
				showIntervalToggle,
				selectedInterval,
				isAnnual,
				currentPlanName,
				currentPlanIsAnnual,
				plan,
				annualMonthlyPrice,
				annualSavingsPercent,
				suggestedUsage,
				hasAverage,
				usageWindowLabel,
				quantities,
				walletAddOns,
				averageRows,
				userTotal,
				usersMax: USERS_MAX,
				addOnMinimums,
				addOnPricingData,
				annualCommitments,
				costModel,
				costLoading,
				costThrottled,
				costStale,
				changePlan,
				submitStatus,
				onCostUpdated,
				onUpdateQuantity,
				nextStep,
				prevStep,
				submitCheckout
			};
		},
		head() {
			return {
				title: 'Customize Plan - Cognito Forms',
				titleTemplate: '%s'
			};
		}
	});
</script>

<style lang="scss">
@import '@cognitoforms/styles/app/common/checkout-flow-layout';

.checkout-flow {
	@include checkout-flow-layout;
	@include checkout-skeleton;
}
</style>
